/**
 * Identity documents — a passport or an ID card the citizen uploads so the
 * platform can fill their profile from it. The contract between brain-service
 * (which reads the document) and platform-api (which stores it and lets the
 * citizen confirm what to use).
 */
import { DOMAIN_ERROR_CODES, apiErrorCode } from '@tonnie/foundation';

export const IDENTITY_DOCUMENT_TYPES = ['passport', 'id_card'] as const;
export type IdentityDocumentType = (typeof IDENTITY_DOCUMENT_TYPES)[number];

export function isIdentityDocumentType(value: unknown): value is IdentityDocumentType {
  return (IDENTITY_DOCUMENT_TYPES as readonly unknown[]).includes(value);
}

/**
 * The largest file read as one side of an identity document. A phone photo or
 * a scan is a few MB; anything bigger is not a document photo, and it would be
 * sent whole to the vision model. The citizen apps check it before uploading;
 * brain-service refuses a larger file with `document_too_large`.
 */
export const IDENTITY_DOCUMENT_MAX_FILE_BYTES = 15 * 1024 * 1024;

/** Why a document could not be read — every refusal carries exactly one. */
export const IDENTITY_DOCUMENT_FAILURE_REASONS = [
  /** No storage key was supplied. */
  'no_input',
  /** The file could not be fetched from storage. */
  'storage_fetch_failed',
  /** A file over IDENTITY_DOCUMENT_MAX_FILE_BYTES. */
  'document_too_large',
  /** Not an image, HEIC, PDF or DOCX (by its bytes). */
  'unsupported_document_type',
  /** A file its parser could not open. */
  'document_unreadable',
  /** More pages than a document is read for. */
  'document_too_many_pages',
  /** A DOCX over the expansion cap. */
  'document_archive_too_large',
  /** A DOCX with nothing to read. */
  'docx_without_text',
  /** The model read the file and it is not a passport or ID card. */
  'not_an_identity_document',
  /** The model's answer was not the JSON asked for. */
  'invalid_model_response',
  /** The provider refused the request. */
  'provider_rejected',
  /** The provider could not be reached or kept failing. */
  'provider_unavailable',
  /** A defect here — reported, never shown as the citizen's fault. */
  'internal_error',
] as const;
export type IdentityDocumentFailureReason = (typeof IDENTITY_DOCUMENT_FAILURE_REASONS)[number];

export function isIdentityDocumentFailureReason(
  value: unknown
): value is IdentityDocumentFailureReason {
  return (IDENTITY_DOCUMENT_FAILURE_REASONS as readonly unknown[]).includes(value);
}

/**
 * What a refusal means to the citizen — the message the apps show. Several
 * reasons share one: the citizen can act on "a clearer photo", not on which
 * parser gave up. `generic` is a failure that is not theirs to fix.
 */
export type IdentityDocumentFailureKind =
  'notIdentity' | 'unreadable' | 'unsupported' | 'tooLarge' | 'unavailable' | 'generic';

export const IDENTITY_DOCUMENT_FAILURE_KIND: Record<
  IdentityDocumentFailureReason,
  IdentityDocumentFailureKind
> = {
  not_an_identity_document: 'notIdentity',
  document_unreadable: 'unreadable',
  document_too_many_pages: 'unreadable',
  docx_without_text: 'unreadable',
  invalid_model_response: 'unreadable',
  unsupported_document_type: 'unsupported',
  document_too_large: 'tooLarge',
  document_archive_too_large: 'tooLarge',
  provider_unavailable: 'unavailable',
  provider_rejected: 'unavailable',
  no_input: 'generic',
  storage_fetch_failed: 'generic',
  internal_error: 'generic',
};

/**
 * A failed read, as the apps receive it, → what to tell the citizen. The
 * server's `reason` rides on the domain error's `data`; any other failure
 * (network, 5xx) is `generic`. Never the message.
 */
export function identityDocumentReadFailure(error: unknown): IdentityDocumentFailureKind {
  if (apiErrorCode(error) !== DOMAIN_ERROR_CODES.IDENTITY_DOCUMENT_NOT_READ) return 'generic';
  const reason = (error as { data?: { reason?: unknown } } | null)?.data?.reason;
  return isIdentityDocumentFailureReason(reason)
    ? IDENTITY_DOCUMENT_FAILURE_KIND[reason]
    : 'generic';
}

/**
 * What the document says. Countries are ISO alpha-2 (null when the code is not
 * one the product knows); dates are `YYYY-MM-DD`.
 */
export interface IdentityDocumentFields {
  surname: string | null;
  givenNames: string | null;
  dateOfBirth: string | null;
  nationality: string | null;
  documentNumber: string | null;
  issuingCountry: string | null;
  issueDate: string | null;
  expiryDate: string | null;
}

/**
 * Which fields the machine-readable zone confirmed: its check digits held for
 * that field (and the zone as a whole). A field read only by the vision model
 * is never verified.
 */
export interface IdentityDocumentVerification {
  name: boolean;
  dateOfBirth: boolean;
  nationality: boolean;
  documentNumber: boolean;
  expiryDate: boolean;
}

export type IdentityDocumentExtraction =
  | {
      success: true;
      /** What the document turned out to be — may differ from what was declared. */
      documentType: IdentityDocumentType;
      fields: IdentityDocumentFields;
      verified: IdentityDocumentVerification;
      /** A machine-readable zone was found and every check digit held. */
      mrzValid: boolean;
    }
  | { success: false; reason: IdentityDocumentFailureReason };
